import { useTranslation } from "react-i18next";
// TodayInPlan — fixed dashboard block (June 2026 social-pivot).
//
// Shows the next upcoming entry on the user's Plan/calendar within the
// next 48 hours. Falls back to a CTA when the calendar is empty.
//
// Calls /api/calendar_events?range=upcoming&limit=1 — the same endpoint
// the Plan page uses, so MongoDB queries stay cache-warm.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Users } from "lucide-react";
import api from "@/api";
const COLOR_BY_KIND = {
  meetup: "#32D74B",
  challenge: "#FFD60A",
  battle: "#FF453A",
  travel: "#0A84FF",
  event: "#BF5AF2"
};
export default function TodayInPlan() {
  const {
    t
  } = useTranslation();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // /api/calendar/events returns the next ~60 days. Filter
        // client-side to the closest *future* one. Backend doesn't
        // have a `range=upcoming` shortcut but the payload is small.
        const {
          data
        } = await api.get("/calendar/events");
        if (cancel) return;
        const list = Array.isArray(data) ? data : data?.events || [];
        const now = Date.now();
        const upcoming = list.filter(e => new Date(e.starts_at || 0).getTime() > now - 3600000).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
        setEvent(upcoming[0] || null);
      } catch {
        if (!cancel) setEvent(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);
  if (loading) {
    return <div className="tile mx-4 mt-3" data-testid="today-in-plan-loading">
                <div className="h-16 animate-pulse rounded-xl bg-foreground/[0.04]" />
            </div>;
  }
  if (!event) {
    // Kaart 2 (PLAN): verberg de tegel volledig wanneer er geen upcoming
    // meet-up is. De bottom-nav bevat al een "Plan"-shortcut en de
    // dashboard-quick-actions bevatten al "Meet-up · Plan together".
    return null;
  }
  const color = COLOR_BY_KIND[event.kind] || "#FFD60A";
  const startsAt = new Date(event.starts_at || event.start || event.created_at);
  const now = new Date();
  const isToday = startsAt.toDateString() === now.toDateString();
  const isTomorrow = startsAt.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  const dayLabel = isToday ? "Vandaag" : isTomorrow ? "Morgen" : startsAt.toLocaleDateString("nl", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
  const timeLabel = startsAt.toLocaleTimeString("nl", {
    hour: "2-digit",
    minute: "2-digit"
  });
  return <button type="button" onClick={() => nav("/plan")} data-testid="today-in-plan" className="tile mx-4 mt-3 w-[calc(100%-2rem)] text-left active:scale-[0.99] transition">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full border flex flex-col items-center justify-center shrink-0" style={{
        borderColor: `${color}55`,
        background: `${color}14`
      }}>
                    <span className="text-[8.5px] uppercase font-bold leading-none" style={{
          color
        }}>
                        {dayLabel.slice(0, 3)}
                    </span>
                    <span className="text-[11px] font-bold mt-0.5" style={{
          color
        }}>
                        {timeLabel}
                    </span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10.5px] uppercase tracking-wider font-bold" style={{
          color
        }}>
                        {event.kind === "meetup" ? "Meet-up" : event.kind === "challenge" ? "Challenge" : event.kind === "battle" ? "Battle" : event.kind === "travel" ? "Reistijd" : "Event"}
                    </p>
                    <p className="text-[14px] font-semibold mt-0.5 truncate">
                        {event.title || "Geen titel"}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                        {event.location && <span className="inline-flex items-center gap-1 truncate">
                                <MapPin size={11} /> {event.location}
                            </span>}
                        {event.attendees?.length > 0 && <span className="inline-flex items-center gap-1">
                                <Users size={11} /> {event.attendees.length}
                            </span>}
                    </div>
                </div>
                <ArrowRight size={16} className="text-muted-foreground shrink-0 mt-1" />
            </div>
        </button>;
}